import { computed } from 'vue'
import { useI18n } from 'vue-i18n'

/**
 * Composable for language management
 * @returns {Object} Language state and methods
 */
export function useLanguage() {
  const { locale, t } = useI18n()

  /**
   * Get current locale
   */
  const currentLocale = computed(() => locale.value)

  /**
   * Check if current language is German
   */
  const isGerman = computed(() => locale.value === 'de')

  /**
   * Check if current language is English
   */
  const isEnglish = computed(() => locale.value === 'en')

  /**
   * Switch language
   * @param {string} newLocale - Language code ('de' or 'en')
   */
  const switchLanguage = (newLocale) => {
    if (!['de', 'en'].includes(newLocale)) {
      console.warn(`Invalid locale: ${newLocale}. Defaulting to 'de'`)
      newLocale = 'de'
    }

    locale.value = newLocale
    localStorage.setItem('locale', newLocale)

    // Update HTML lang attribute for accessibility
    document.documentElement.setAttribute('lang', newLocale)

    console.log(`Language switched to: ${newLocale}`)
  }

  /**
   * Toggle between German and English
   */
  const toggleLanguage = () => {
    const newLocale = locale.value === 'de' ? 'en' : 'de'
    switchLanguage(newLocale)
  }

  /**
   * Get localized link based on current language
   * @param {string} key - Translation key for the link
   * @returns {string} Localized link
   */
  const getLocalizedLink = (key) => {
    return t(`links.${key}`)
  }

  return {
    currentLocale,
    isGerman,
    isEnglish,
    switchLanguage,
    toggleLanguage,
    getLocalizedLink,
    t
  }
}
